import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { Council, CouncilMember, CouncilMemberWithPosition, useCouncilStore } from '../../store/useCouncilStore';
import { FamilyMember, useFamilyMemberStore } from '../../store/useFamilyMemberStore';
import { Button, Label, Modal, Popover, TextInput, Textarea } from 'flowbite-react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCollegeStore } from '../../store/useCollegeStore';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';
import MultiSelect from '../../components/form/MultiSelect';

export default function CouncilPage() {
  const navigate = useNavigate();
  const getCouncils = useCouncilStore((state) => state.getCouncils);
  const deleteCouncil = useCouncilStore((state) => state.deleteCouncil);
  const councils = useCouncilStore((state) => state.councils);
  const createCouncil = useCouncilStore((state) => state.createCouncil);
  const updateCouncil = useCouncilStore((state) => state.updateCouncil);
  const loading = useCouncilStore((state) => state.loading);
  const pagination = useCouncilStore((state) => state.pagination);
  const uploadFile = useCollegeStore((state) => state.uploadFile);
  const uploadedFile = useCollegeStore((state) => state.uploadedFile);
  const getFamilyMembers = useFamilyMemberStore((state) => state.getFamilyMembers);
  const familyMembers = useFamilyMemberStore((state) => state.members);

  const canAccess = useCanAccessResource('council');
  const hasReadPermission = useHasPermission('council', 'read');
  const hasCreatePermission = useHasPermission('council', 'create');
  const hasUpdatePermission = useHasPermission('council', 'update');
  const hasDeletePermission = useHasPermission('council', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [tableData, setTableData] = useState<Council[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCouncil, setSelectedCouncil] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);

  const [period, setPeriod] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Array<{ member?: string; name?: string; position: string }>>([]);
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [manualMemberName, setManualMemberName] = useState('');
  const [manualMemberPosition, setManualMemberPosition] = useState('');

  // Fetch all family members for the multi-select
  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getFamilyMembers(undefined, undefined, 1, 1000); // Fetch all members
    }
  }, [getFamilyMembers, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getCouncils(undefined, search || undefined, currentPage, limit);
    }
  }, [getCouncils, search, currentPage, limit, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    setTableData(councils);
  }, [councils]);

  const handleAddOrEdit = async () => {
    if (!period || selectedMembers.length === 0) {
      toast.error('Please fill in all required fields (Period and at least one member).', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    // Format members for API (ensure position is included even if empty)
    const formattedMembers = selectedMembers.map((m) => ({
      ...(m.member ? { member: m.member } : {}),
      ...(m.name ? { name: m.name } : {}),
      position: m.position || ''
    }));

    if (modalType === 'add') {
      await createCouncil({ period, members: formattedMembers, description, image: image || '' });
    } else {
      if (!editId) return;
      await updateCouncil(editId, { period, members: formattedMembers, description, image: image || '' });
    }

    setOpenAddModal(false);
    setPeriod('');
    setSelectedMembers([]);
    setDescription('');
    setImage('');
    setEditId(null);
    setModalType('add');
    setIsUploading(false);
    setManualMemberName('');
    setManualMemberPosition('');
    useCollegeStore.setState({ uploadedFile: null });
  };

  const handleEdit = (council: Council) => {
    setEditId(council._id);
    setModalType('edit');
    setOpenAddModal(true);
    setPeriod(council.period);
    // Extract member IDs/names and positions from the council members array
    const membersWithPositions = council.members.map((member) => {
      // Handle new format: { member: {...}, name: "...", position: "..." }
      if (typeof member === 'object' && ('member' in member || 'name' in member)) {
        const memberObj = member as CouncilMemberWithPosition;
        // Check if it's a manual member (has name) or system member (has member)
        if (memberObj.name) {
          return { name: memberObj.name, position: memberObj.position || '' };
        } else if (memberObj.member) {
          const memberId = typeof memberObj.member === 'string' ? memberObj.member : memberObj.member._id;
          return { member: memberId, position: memberObj.position || '' };
        }
      }
      // Handle old format: just member ID or member object
      if (typeof member === 'string') {
        return { member, position: '' };
      } else if (typeof member === 'object' && 'name' in member) {
        const memberData = member as CouncilMember;
        return { member: memberData._id, position: '' };
      }
      return { member: '', position: '' };
    });
    setSelectedMembers(membersWithPositions);
    setDescription(council.description || '');
    setImage(council.image || '');
  };

  const handleDelete = () => {
    if (selectedCouncil !== null) {
      deleteCouncil(selectedCouncil);
      setOpenModal(false);
      setSelectedCouncil(null);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error('File size exceeds the maximum limit of 5MB. Please upload a smaller file.', {
          duration: 3000,
          icon: '❌',
        });
        e.target.value = ''; // Reset file input
        return;
      }

      setIsUploading(true);
      try {
        await uploadFile(file);
      } catch (error) {
        // Error will be handled by displayErrorToast in the API function
      } finally {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    if (uploadedFile?.data?.upload) {
      const upload = uploadedFile.data.upload as any;
      // Handle different response formats (ImageKit returns url, S3 returns Location, old format has uploadUrl)
      if (upload.url) {
        setImage(upload.url);
      } else if (upload.Location) {
        setImage(upload.Location);
      } else if (upload.uploadUrl) {
        setImage(upload.uploadUrl);
      }
    }
  }, [uploadedFile]);

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Council" />;
  }

  // Prepare options for MultiSelect
  const memberOptions = familyMembers.map((member) => ({
    value: member._id,
    text: member.name,
    selected: false, // MultiSelect uses defaultSelected prop instead
  }));

  // Handle member selection from MultiSelect
  const handleMemberSelection = (selectedIds: string[]) => {
    // Keep existing positions for members that are still selected (system members only)
    const existingMembers = selectedMembers.filter((m) => m.member && selectedIds.includes(m.member));
    const newMemberIds = selectedIds.filter((id) => !existingMembers.some((m) => m.member === id));
    
    // Add new members without positions
    const newMembers = newMemberIds.map((id) => ({ member: id, position: '' }));
    
    // Keep manual members (those with name but no member)
    const manualMembers = selectedMembers.filter((m) => m.name && !m.member);
    
    setSelectedMembers([...existingMembers, ...newMembers, ...manualMembers]);
  };

  // Add manual member
  const handleAddManualMember = () => {
    if (!manualMemberName.trim()) {
      toast.error('Please enter a member name', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }
    
    const newManualMember = {
      name: manualMemberName.trim(),
      position: manualMemberPosition.trim()
    };
    
    setSelectedMembers([...selectedMembers, newManualMember]);
    setManualMemberName('');
    setManualMemberPosition('');
  };

  // Update position for a specific member (by member ID or name)
  const handlePositionChange = (identifier: string, position: string, isManual: boolean = false) => {
    setSelectedMembers((prev) =>
      prev.map((m) => {
        if (isManual && m.name === identifier) {
          return { ...m, position };
        } else if (!isManual && m.member === identifier) {
          return { ...m, position };
        }
        return m;
      })
    );
  };

  // Remove a member (by member ID or name)
  const handleRemoveMember = (identifier: string, isManual: boolean = false) => {
    setSelectedMembers((prev) => 
      prev.filter((m) => {
        if (isManual) {
          return m.name !== identifier;
        } else {
          return m.member !== identifier;
        }
      })
    );
  };

  // Get member name by ID or return the name if it's a manual member
  const getMemberName = (memberWithPosition: { member?: string; name?: string; position: string }) => {
    if (memberWithPosition.name) {
      return memberWithPosition.name;
    }
    if (memberWithPosition.member) {
      const member = familyMembers.find((m) => m._id === memberWithPosition.member);
      return member?.name || 'Unknown';
    }
    return 'Unknown';
  };

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedCouncil(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this council?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedCouncil(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="lg"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setPeriod('');
          setSelectedMembers([]);
          setDescription('');
          setImage('');
          setEditId(null);
          setModalType('add');
          setIsUploading(false);
          setManualMemberName('');
          setManualMemberPosition('');
          useCollegeStore.setState({ uploadedFile: null });
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add Council' : 'Edit Council'}
            </h3>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="period" value="Period *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="period"
                type="text"
                placeholder="e.g., 2023-2025"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="members" value="Council Members *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <MultiSelect
                label=""
                options={memberOptions}
                defaultSelected={selectedMembers.filter((m) => m.member).map((m) => m.member!)}
                onChange={handleMemberSelection}
              />
              
              {/* Manual Member Addition */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label value="Add Manual Member (Not in System)" style={{ fontFamily: 'Lato, sans-serif' }} />
                <div className="mt-2 space-y-2">
                  <TextInput
                    type="text"
                    placeholder="Enter member name"
                    value={manualMemberName}
                    onChange={(e) => setManualMemberName(e.target.value)}
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                  <TextInput
                    type="text"
                    placeholder="Enter position (optional)"
                    value={manualMemberPosition}
                    onChange={(e) => setManualMemberPosition(e.target.value)}
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  />
                  <Button
                    onClick={handleAddManualMember}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                  >
                    Add Manual Member
                  </Button>
                </div>
              </div>
              
              {/* Show selected members with position inputs */}
              {selectedMembers.length > 0 && (
                <div className="mt-4 space-y-3">
                  <Label value="Member Positions (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
                  {selectedMembers.map((memberWithPosition, index) => {
                    const isManual = !!memberWithPosition.name;
                    const identifier = isManual ? memberWithPosition.name! : memberWithPosition.member!;
                    const uniqueKey = isManual ? `manual-${memberWithPosition.name}` : `member-${memberWithPosition.member}`;
                    
                    return (
                      <div key={uniqueKey} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="text-sm font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                              {getMemberName(memberWithPosition)}
                            </div>
                            {isManual && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded" style={{ fontFamily: 'Lato, sans-serif' }}>
                                Manual
                              </span>
                            )}
                          </div>
                          <TextInput
                            type="text"
                            placeholder="Enter position (e.g., President, Secretary, Treasurer)"
                            value={memberWithPosition.position}
                            onChange={(e) => handlePositionChange(identifier, e.target.value, isManual)}
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          />
                        </div>
                        <Button
                          color="failure"
                          size="sm"
                          onClick={() => handleRemoveMember(identifier, isManual)}
                          className="flex-shrink-0"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="description" value="Description (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Textarea
                id="description"
                placeholder="Enter description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="image" value="Image (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              {isUploading ? (
                <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B] mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Uploading image...
                  </p>
                </div>
              ) : image ? (
                <div className="mb-2">
                  <img src={image} alt="Council" className="w-full h-48 object-cover rounded-lg" />
                  <Button color="failure" size="sm" onClick={() => setImage('')} className="mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Remove Image
                  </Button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#B8860B] file:text-white hover:file:bg-[#9A7209] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              )}
            </div>

            <div className="w-full">
              <Button
                onClick={handleAddOrEdit}
                disabled={loading || isUploading || !period || selectedMembers.length === 0}
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : isUploading ? 'Uploading...' : modalType === 'add' ? 'Add Council' : 'Edit Council'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="Council" description="Manage council" />
      <PageBreadcrumb pageTitle="Council" />
      <div className="space-y-6">
        <ComponentCard title="Council">
          <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5">
            {(hasCreatePermission || isMainAdmin) && (
              <Button
                onClick={() => {
                  setOpenAddModal(true);
                  setModalType('add');
                  setPeriod('');
                  setSelectedMembers([]);
                  setDescription('');
                  setImage('');
                  setEditId(null);
                  setManualMemberName('');
                  setManualMemberPosition('');
                }}
                className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                Add Council
              </Button>
            )}
            <div className="relative">
              <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                <svg
                  className="fill-gray-500 dark:fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                    fill=""
                  />
                </svg>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search council..."
                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1000px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Image
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Period
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Members
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No councils found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((council) => (
                        <TableRow key={council._id}>
                          <TableCell className="px-5 py-4 sm:px-6 text-start">
                            {council.image ? (
                              <img src={council.image} alt={`${council.period}`} className="w-24 h-16 object-cover rounded" />
                            ) : (
                              <div className="w-24 h-16 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                <span className="text-gray-400 text-xs">No Image</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {council.period}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            <div className="flex flex-wrap gap-1">
                              {council.members.map((member, index) => {
                                // Handle new format: { member: {...}, name: "...", position: "..." }
                                let memberName = 'Unknown';
                                let position = '';
                                let memberId = '';
                                let isManual = false;

                                if (typeof member === 'object' && ('member' in member || 'name' in member)) {
                                  // New format with position
                                  const memberObj = member as CouncilMemberWithPosition;
                                  
                                  // Check if it's a manual member (has name but no member)
                                  if (memberObj.name && !memberObj.member) {
                                    isManual = true;
                                    memberName = memberObj.name;
                                    position = memberObj.position || '';
                                  } else if (memberObj.member) {
                                    // System member
                                    const memberData = typeof memberObj.member === 'object' && 'name' in memberObj.member ? memberObj.member : null;
                                    memberName = memberData ? memberData.name : 'Unknown';
                                    memberId = typeof memberObj.member === 'string' ? memberObj.member : (memberData?._id || '');
                                    position = memberObj.position || '';
                                  }
                                } else if (typeof member === 'object' && 'name' in member) {
                                  // Old format: just member object (CouncilMember)
                                  const memberData = member as CouncilMember;
                                  memberName = memberData.name;
                                  memberId = memberData._id || '';
                                } else if (typeof member === 'string') {
                                  // Old format: just member ID
                                  memberId = member;
                                }

                                return (
                                  <span
                                    key={isManual ? `manual-${memberName}-${index}` : memberId || index}
                                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded"
                                  >
                                    {memberName}
                                    {isManual && <span className="text-blue-600 ml-1 text-[10px]">(Manual)</span>}
                                    <span className="text-[#B8860B] ml-1">({position || 'Member'})</span>
                                  </span>
                                );
                              })}
                            </div>
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {dayjs(council.createdAt).format('DD MMM YYYY')}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <Popover
                                placement="top"
                                aria-labelledby="default-popover"
                                content={
                                  <div className="py-2 px-3">
                                    {(hasDeletePermission || isMainAdmin) && (
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => {
                                          setSelectedCouncil(council._id);
                                          setOpenModal(true);
                                        }}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-trash-2"
                                        >
                                          <path d="M3 6h18" />
                                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                          <line x1="10" x2="10" y1="11" y2="17" />
                                          <line x1="14" x2="14" y1="11" y2="17" />
                                        </svg>
                                        Delete
                                      </div>
                                    )}

                                    {(hasUpdatePermission || isMainAdmin) && (
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => handleEdit(council)}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-pencil"
                                        >
                                          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                          <path d="m15 5 4 4" />
                                        </svg>
                                        Edit
                                      </div>
                                    )}
                                  </div>
                                }
                              >
                                <button
                                  className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                  style={{ fontFamily: 'Lato, sans-serif' }}
                                >
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </button>
                              </Popover>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
